/* import __COLOCATED_TEMPLATE__ from './feature-comparison.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import ENV from 'embercom/config/environment';
import type IntlService from 'embercom/services/intl';
import type MarketingFeaturesService from 'embercom/services/marketing-features-service';
import {
  PRICING_5_X_CORE_ESSENTIAL_ID,
  PRICING_5_X_CORE_ADVANCED_ID,
  PRICING_5_X_CORE_EXPERT_ID,
  PROACTIVE_SUPPORT_PLUS_BASE_ID,
  PERFORMANCE_PRO_BASE_ID,
} from 'embercom/lib/billing';

export interface AddonData {
  name: string;
  proactiveSupportPlusHCLink: string;
  tooltipContent: string;
}

interface Args {
  proactiveSupportFullPrice: number;
}

interface Signature {
  Args: Args;
}

export default class EarlyStageProgressionFeatureComparisonComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare purchaseAnalyticsService: $TSFixMe;
  @service declare marketingFeaturesService: MarketingFeaturesService;
  @tracked searchValue = '';
  @tracked debouncedSearchValue = '';

  get proactiveSupportPlusArticleId() {
    return 9061648; // https://www.intercom.com/help/articles/9061648
  }

  get aiInsightsHelpdeskArticleId() {
    return 13003501; // https://www.intercom.com/help/articles/13003501
  }

  get tableData() {
    let data: any = [];
    let features = this.marketingFeaturesService.features;
    let allFeatures = Object.keys(features);

    allFeatures.forEach((featureKey) => {
      let feature = features[featureKey];
      let featureTitle = feature.title;

      if (
        this.debouncedSearchValue &&
        !featureTitle.toLowerCase().includes(this.debouncedSearchValue.toLowerCase()) &&
        !featureKey.toLowerCase().includes(this.debouncedSearchValue.toLowerCase())
      ) {
        return;
      }

      let isEssential = this.marketingFeaturesService.isFeatureInPlan(
        featureKey,
        Number(PRICING_5_X_CORE_ESSENTIAL_ID),
      );
      let isAdvanced = this.marketingFeaturesService.isFeatureInPlan(
        featureKey,
        Number(PRICING_5_X_CORE_ADVANCED_ID),
      );
      let isExpert = this.marketingFeaturesService.isFeatureInPlan(
        featureKey,
        Number(PRICING_5_X_CORE_EXPERT_ID),
      );
      let isPSPFeature = this.marketingFeaturesService.isFeatureInPlan(
        featureKey,
        Number(PROACTIVE_SUPPORT_PLUS_BASE_ID),
      );
      let isPerformanceProFeature =
        this.appService.app.canSeeAiInsightsAddon &&
        this.marketingFeaturesService.isFeatureInPlan(featureKey, Number(PERFORMANCE_PRO_BASE_ID));

      if (!isEssential && !isAdvanced && !isExpert && !isPSPFeature && !isPerformanceProFeature) {
        return;
      }

      data.push({
        feature_key: featureKey,
        feature: featureTitle,
        feature_tooltip: feature.description,
        currentPlan: isAdvanced,
        essentialPlan: isEssential,
        advancedPlan: isEssential || isAdvanced,
        expertPlan: isEssential || isAdvanced || isExpert,
        proactivePlan: isPSPFeature,
        performanceProPlan: isPerformanceProFeature,
      });
    });

    data = data.sort((a: any, b: any) =>
      a.feature.localeCompare(b.feature, undefined, { sensitivity: 'base' }),
    );

    return data;
  }

  get proactiveSupportPlusData(): AddonData {
    return {
      name: this.intl.t('billing.migrations.proactive_support_plus'),
      proactiveSupportPlusHCLink: this.proactiveSupportPlusHCLink,
      tooltipContent: this.intl.t('billing.migrations.proactive_support_plus_tooltip', {
        price: this.args.proactiveSupportFullPrice,
        htmlSafe: true,
      }),
    };
  }

  get proactiveSupportPlusHCLink(): string {
    return 'https://www.intercom.com/help/en/articles/9061648-proactive-support-plus';
  }

  @action
  onChangeSearchValue() {
    debounce(this, this.updateSearchValue, ENV.APP.__250MS);
  }

  updateSearchValue() {
    this.debouncedSearchValue = this.searchValue;
    this.purchaseAnalyticsService.trackEvent({
      action: 'search',
      context: 'early_stage_progression',
      place: 'update_subscription',
      object: 'feature_comparison',
      value: this.debouncedSearchValue,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'billing/update-early-stage-subscription/feature-comparison': typeof EarlyStageProgressionFeatureComparisonComponent;
    'Billing::UpdateEarlyStageSubscription::FeatureComparison': typeof EarlyStageProgressionFeatureComparisonComponent;
  }
}
